import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import TextField from '@mui/material/TextField';

import {
  Accordion, AccordionSummary, AccordionDetails, Typography, TextareaAutosize,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import { useTranslation } from '../common/components/LocalizationProvider';
import SettingsMenu from './components/SettingsMenu';
import { useCatch } from '../reactHelper';
import { poilocationsActions } from '../store';
import SelectField from '../common/components/SelectField';
import SelectColor from '../common/components/SelectColor';
import markerIcons from '../common/util/markerIcons';
import palette from '../common/theme/palette';

const useStyles = makeStyles((theme) => ({
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  textarea: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(0),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
}));

const POILocationPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const t = useTranslation();

  const [item, setItem] = useState();

  const onItemSaved = useCatch(async () => {
    const response = await fetch('/api/poilocations');
    if (response.ok) {
      dispatch(poilocationsActions.update(await response.json()));
    } else {
      throw Error(await response.text());
    }
  });

  const validate = () => item && item.name;

  return (
    <EditItemView
      endpoint="poilocations"
      item={item}
      setItem={setItem}
      validate={validate}
      onItemSaved={onItemSaved}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'poilocationDialog']}
    >
      {item && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedRequired')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                value={item.name || ''}
                onChange={(event) => setItem({ ...item, name: event.target.value })}
                label={t('sharedName')}
              />
            </AccordionDetails>
            <AccordionDetails className={classes.details}>
              <TextField
                value={item.description || ''}
                onChange={(event) => setItem({ ...item, description: event.target.value })}
                label={t('sharedDescription')}
              />
            </AccordionDetails>
            <AccordionDetails className={classes.details}>
              <SelectField
                endpoint="/api/poigroups?all=true"
                value={item.poiGroupId}
                label={t('group')}
                emptyValue={false}
                onChange={(event) => setItem({ ...item, poiGroupId: event.target.value })}
              />
            </AccordionDetails>
            <AccordionDetails className={classes.details}>
              <TextField
                value={item.latitude || ''}
                onChange={(event) => setItem({ ...item, latitude: event.target.value })}
                label={t('latitude')}
              />
            </AccordionDetails>
            <AccordionDetails className={classes.details}>
              <TextField
                value={item.longitude || ''}
                onChange={(event) => setItem({ ...item, longitude: event.target.value })}
                label={t('longitude')}
              />
            </AccordionDetails>
            <AccordionDetails className={classes.details}>
              <SelectField
                value={item.icon || 'default'}
                emptyValue={null}
                onChange={(event) => setItem({ ...item, icon: event.target.value })}
                data={markerIcons.map((icon) => ({
                  id: icon,
                  name: t(`marker${icon.replace(/^\w/, (c) => c.toUpperCase())}`),
                }))}
                label={t('icon')}
              />
            </AccordionDetails>
            <AccordionDetails className={classes.details}>
              <SelectColor
                value={item.color || 'white'}
                onChange={(event) => setItem({ ...item, color: event.target.value })}
                data={Object.entries(palette.markerColors).map(([key, value]) => ({
                  id: key,
                  name: key,
                  description: value,
                }))}
                label={t('attributeColor')}
              />
            </AccordionDetails>
            <AccordionDetails className={classes.textarea}>
              <span><h4>HTML Popup</h4></span>
              <TextareaAutosize
                value={item.html || ''}
                sx={{ whiteSpace: 'new-line' }}
                placeholder={`<html>\n\t<body>\n\t\t<h2>${item.name}</h2>\n\t</body>\n</html>`}
                onChange={(event) => setItem({ ...item, html: event.target.value })}
                label={t('html')}
              />
            </AccordionDetails>
          </Accordion>
          <EditAttributesAccordion
            attributes={item.attributes}
            setAttributes={(attributes) => setItem({ ...item, attributes })}
            definitions={{}}
          />
        </>
      )}
    </EditItemView>
  );
};

export default POILocationPage;
